import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminCopy } from "@/contexts/AdminCopyContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import * as LucideIcons from "lucide-react";
import { 
  Sparkles, Scissors, Palette, Eye, PartyPopper, Heart, Leaf, 
  ShoppingBag, UserCheck, Utensils, ArrowRight, Check, MapPin, Plane
} from "lucide-react";
import axios from "axios";
import useExperienceCategories, { ICON_MAP } from "@/hooks/useExperienceCategories";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Iter 9.47.p - Fallback service grid used only when /api/experience-categories
// returns nothing (offline / first-paint). Once the hook resolves, this is
// replaced with the live category list so the modal NEVER drifts out of step
// with the master catalog.
const FALLBACK_SERVICES = [
  { id: "hair", label: "Hair", icon: Scissors, color: "bg-purple-100 text-purple-600" },
  { id: "nails", label: "Nails", icon: Palette, color: "bg-pink-100 text-pink-600" },
  { id: "makeup", label: "Makeup", icon: Sparkles, color: "bg-rose-100 text-rose-600" },
  { id: "lashes", label: "Lashes", icon: Eye, color: "bg-amber-100 text-amber-600" },
  { id: "events", label: "Events", icon: PartyPopper, color: "bg-blue-100 text-blue-600" },
  { id: "wellness", label: "Wellness", icon: Heart, color: "bg-green-100 text-green-600" },
  { id: "dispensary", label: "Dispensary", icon: Leaf, color: "bg-emerald-100 text-emerald-600" },
  { id: "style", label: "Retail", icon: ShoppingBag, color: "bg-orange-100 text-orange-600" },
  { id: "travel", label: "Travel", icon: Plane, color: "bg-sky-100 text-sky-600" },
  { id: "instructor", label: "Instructor", icon: UserCheck, color: "bg-indigo-100 text-indigo-600" },
  { id: "culinary", label: "Culinary", icon: Utensils, color: "bg-amber-100 text-amber-700" }
];

const COLOR_PALETTE = [
  "bg-purple-100 text-purple-600",
  "bg-pink-100 text-pink-600",
  "bg-rose-100 text-rose-600",
  "bg-amber-100 text-amber-600",
  "bg-blue-100 text-blue-600",
  "bg-green-100 text-green-600",
  "bg-emerald-100 text-emerald-600",
  "bg-orange-100 text-orange-600",
  "bg-sky-100 text-sky-600",
  "bg-indigo-100 text-indigo-600",
  "bg-amber-100 text-amber-700",
];

// Category-specific sales language
const CATEGORY_SALES_LANGUAGE = {
  hair: { headline: "Join as a Hair Stylist", subtitle: "Get early access to manage client hair profiles, textures, and style preferences digitally." },
  makeup: { headline: "Join as a Makeup Artist", subtitle: "Collect skin tones, coverage preferences, and bridal looks before your clients arrive." },
  nails: { headline: "Join as a Nail Technician", subtitle: "Capture nail shape, length, and design preferences. No more guessing at the chair." },
  lashes: { headline: "Join as a Lash Technician", subtitle: "Know your client's curl, length, and material preferences before every set." },
  events: { headline: "Join as an Event Planner", subtitle: "Collect dietary needs, accessibility requirements, and guest preferences for every event." },
  wellness: { headline: "Join as a Wellness Provider", subtitle: "Manage client health profiles, pressure preferences, and wellness goals digitally." },
  culinary: { headline: "Join as a Culinary Professional", subtitle: "Capture dietary restrictions, allergies, and flavor preferences before you cook." },
  travel: { headline: "Join as a Travel Curator", subtitle: "Collect travel styles, accommodation preferences, and accessibility needs from your clients." },
  dispensary: { headline: "Join as a Dispensary Provider", subtitle: "Know your client's consumption preferences and product interests upfront." },
  retail: { headline: "Join as a Retail Stylist", subtitle: "Capture style preferences, sizes, and budget ranges for personalized shopping." },
  instructor: { headline: "Join as an Instructor", subtitle: "Collect student skill levels, learning goals, and accessibility needs before class." },
};

const EarlyAccessModal = ({ isOpen, onClose, open, onOpenChange, category }) => {
  const t = useAdminCopy();
  const c = t; // alias for slotText fallback resolver below
  // Iter 9.47.p - services sync from the live category catalog; copy + field
  // visibility from `surface_provider_waitlist` Airtable surface. Falls back
  // to FALLBACK_SERVICES for offline / first-paint and to English defaults
  // for missing slots.
  const { slotMap: pw } = useUiSurface("surface_provider_waitlist");
  const { visibleCategories, loading: catsLoading } = useExperienceCategories();

  const serviceOptions = useMemo(() => {
    if (!visibleCategories || visibleCategories.length === 0) return FALLBACK_SERVICES;
    return visibleCategories.map((cat, idx) => {
      const iconName = ICON_MAP[(cat.icon || "default").toLowerCase()] || "Sparkles";
      const Icon = LucideIcons[iconName] || Sparkles;
      return {
        id: cat.id,
        label: cat.name || cat.id,
        // Iter 9.48 - prefer the uploaded category logo IMAGE (Cloudinary)
        // over the lucide icon. Icon stays as fallback when logo is missing.
        logoUrl: cat.default_logo_url || cat.logo_url || cat.icon_url || null,
        icon: Icon,
        // Iter 9.48 - use Airtable category colors (dark_hex on selection,
        // light_hex on background) so the popup matches the category palette.
        darkHex: cat.dark_hex,
        lightHex: cat.light_hex,
        // Legacy tailwind palette fallback
        color: COLOR_PALETTE[idx % COLOR_PALETTE.length],
      };
    });
  }, [visibleCategories]);

  // Field visibility / required toggles (string booleans from Airtable).
  const showCompany    = slotText(pw, "field_company_show",    "true").toLowerCase() !== "false";
  const requireCompany = slotText(pw, "field_company_required", "false").toLowerCase() === "true";
  const showInstagram  = slotText(pw, "field_instagram_show",  "true").toLowerCase() !== "false";

  // City suggestions list comes from Airtable (CSV).
  // Iter 9.48 - properly parse "City, State" pairs back together so the
  // pills show "Los Angeles, CA" instead of "Los Angeles" | "CA" separately.
  const popularCities = useMemo(() => {
    const csv = slotText(pw, "city_suggestions_csv",
      "Los Angeles, CA, New York, NY, Miami, FL, Atlanta, GA");
    const tokens = csv.split(",").map(s => s.trim()).filter(Boolean);
    const pairs = [];
    let i = 0;
    while (i < tokens.length) {
      const cur = tokens[i];
      const next = tokens[i + 1] || "";
      // If next token looks like a 2-letter state code, pair them
      if (/^[A-Z]{2}$/.test(next)) {
        pairs.push(`${cur}, ${next}`);
        i += 2;
      } else {
        pairs.push(cur);
        i += 1;
      }
    }
    return pairs;
  }, [pw]);
  // Support both prop patterns: (isOpen/onClose) and (open/onOpenChange)
  const isModalOpen = isOpen ?? open ?? false;
  const handleClose = onClose || onOpenChange;
  
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    services: category ? [category] : [],
    otherServiceDescription: "",
    instagram: "",
    city: "",
    company: "",
    agreedToTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [smartRoute, setSmartRoute] = useState(null);
  const [citySearch, setCitySearch] = useState("");
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);

  // Popular cities are now sourced from Airtable (`city_suggestions_csv`).
  // The legacy hardcoded list moved into the `popularCities` useMemo above.

  const handleServiceToggle = (serviceId) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handleCitySearch = async (value) => {
    setCitySearch(value);
    if (value.length >= 1) {
      try {
        // Iter 9.48 - allow 1-char search (was 2). Backend still requires 2,
        // so short queries fall back to the static popularCities list immediately.
        if (value.length >= 2) {
          const response = await axios.get(`${API}/places/autocomplete?input=${encodeURIComponent(value)}&types=cities`);
          setCitySuggestions(response.data.predictions || []);
        } else {
          const staticCities = popularCities.filter(c =>
            c.toLowerCase().startsWith(value.toLowerCase())
          );
          setCitySuggestions(staticCities.map(c => ({ description: c })));
        }
        setShowCitySuggestions(true);
      } catch (error) {
        const staticCities = popularCities.filter(c =>
          c.toLowerCase().includes(value.toLowerCase())
        );
        setCitySuggestions(staticCities.map(c => ({ description: c })));
        setShowCitySuggestions(true);
      }
    } else {
      setShowCitySuggestions(false);
    }
  };

  const selectCity = (city) => {
    const cityName = typeof city === 'string' ? city : city.description;
    setForm(prev => ({ ...prev, city: cityName }));
    setCitySearch(cityName);
    setShowCitySuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.agreedToTerms) {
      alert("Please agree to the terms to continue.");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/waitlist/join`, {
        ...form,
        name: `${form.first_name} ${form.last_name}`.trim(),
        user_type: "provider",
        service_categories: form.services,
        other_service_description: form.otherServiceDescription,
        consent_given: true,
        beehiiv_consent: true,
        consent_timestamp: new Date().toISOString()
      });
      // Smart routing - check if primary category is launched in city
      try {
        const primary = form.services[0];
        if (primary && form.city) {
          const sr = await axios.get(
            `${API}/api/launch-status/smart-route?category=${primary}&city=${encodeURIComponent(form.city)}`
          );
          if (sr.data?.launch_status === "launched") {
            // Category is LIVE in their city -> send them to subscription intake path
            setSmartRoute({
              ...sr.data,
              destination: `/host/signup?category=${primary}&city=${encodeURIComponent(form.city)}`,
            });
          } else {
            // Iter 9.36 - always land on /me dashboard with waitlist banner so user is never stranded
            setSmartRoute({
              ...sr.data,
              destination: `/me?waitlist=joined&category=${encodeURIComponent(primary)}`,
            });
          }
        } else {
          // Fallback when no city/primary - still redirect to /me
          setSmartRoute({ destination: "/me?waitlist=joined" });
        }
      } catch (routeErr) {
        console.warn("smart-route lookup failed", routeErr);
        setSmartRoute({ destination: "/me?waitlist=joined" });
      }
      setSubmitSuccess(true);
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setForm({ first_name: "", last_name: "", phone: "", email: "", services: [], otherServiceDescription: "", instagram: "", city: "", company: "", agreedToTerms: false });
    setCitySearch("");
    setSubmitSuccess(false);
    onOpenChange(false);
  };

  const isFormValid =
    form.first_name &&
    form.last_name &&
    form.email &&
    form.phone &&
    form.services.length > 0 &&
    form.city &&
    form.agreedToTerms &&
    (!requireCompany || (form.company || "").trim());

  return (
    <Dialog open={isModalOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-white text-black border-0 shadow-2xl rounded-2xl p-0 max-h-[95vh] overflow-hidden">
        {!submitSuccess ? (
          <div className="flex flex-col h-full">
            {/* Compact Header */}
            <div className="bg-gradient-to-r from-gray-900 to-black px-5 py-4 text-center">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-white/10 rounded-full text-white/80 text-[10px] font-medium mb-2" data-testid="early-access-badge">
                <Sparkles className="w-2.5 h-2.5" />
                {slotText(pw, "badge_label", "Early Access")}
              </div>
              <h2 className="text-lg font-bold text-white" data-testid="early-access-title">{(category && CATEGORY_SALES_LANGUAGE[category]?.headline) || slotText(pw, "hero_title", "Join the Provider Waitlist")}</h2>
              {category && CATEGORY_SALES_LANGUAGE[category]?.subtitle && (
                <p className="text-xs text-white/70 mt-1">{CATEGORY_SALES_LANGUAGE[category].subtitle}</p>
              )}
              {!category && (
                <p className="text-xs text-white/70 mt-1" data-testid="early-access-subtitle">{slotText(pw, "hero_subtitle", "Help us shape the platform for service providers like you.")}</p>
              )}
            </div>

            {/* Form - Compact Layout */}
            <form onSubmit={handleSubmit} className="p-4 space-y-3 overflow-y-auto flex-1">
              {/* First Name & Last Name Row */}
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={form.first_name}
                  onChange={(e) => setForm(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder={`${slotText(pw, "field_first_name_label", "First Name")} *`}
                  required
                  className="h-9 text-sm bg-gray-50 border-gray-200 rounded-lg"
                  data-testid="early-access-first-name"
                />
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder={`${slotText(pw, "field_last_name_label", "Last Name")} *`}
                  required
                  className="h-9 text-sm bg-gray-50 border-gray-200 rounded-lg"
                  data-testid="early-access-last-name"
                />
              </div>

              {/* Email & Phone Row */}
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder={`${slotText(pw, "field_email_label", "Email")} *`}
                  required
                  className="h-9 text-sm bg-gray-50 border-gray-200 rounded-lg"
                  data-testid="early-access-email"
                />
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder={`${slotText(pw, "field_phone_label", "Phone")} *`}
                  required
                  className="h-9 text-sm bg-gray-50 border-gray-200 rounded-lg"
                  data-testid="early-access-phone"
                />
              </div>

              {/* Company Name (admin-controlled show + required) */}
              {showCompany && (
                <Input
                  value={form.company || ""}
                  onChange={(e) => setForm(prev => ({ ...prev, company: e.target.value }))}
                  placeholder={`${slotText(pw, "field_company_label", "Company / Brand Name")}${requireCompany ? " *" : " (optional)"}`}
                  required={requireCompany}
                  className="h-9 text-sm bg-gray-50 border-gray-200 rounded-lg"
                  data-testid="early-access-company"
                />
              )}

              {/* Instagram (admin-controlled show) */}
              {showInstagram && (
                <div className="flex">
                  <span className="inline-flex items-center px-2 bg-gray-100 border border-r-0 border-gray-200 rounded-l-lg text-gray-400 text-xs">@</span>
                  <Input
                    value={form.instagram}
                    onChange={(e) => setForm(prev => ({ ...prev, instagram: e.target.value }))}
                    placeholder={`${slotText(pw, "field_instagram_label", "instagram")} (optional)`}
                    className="rounded-l-none h-9 text-sm bg-gray-50 border-gray-200 rounded-r-lg"
                    data-testid="early-access-instagram"
                  />
                </div>
              )}

              {/* City with Autocomplete */}
              <div className="relative">
                <div className="flex">
                  <span className="inline-flex items-center px-2 bg-gray-100 border border-r-0 border-gray-200 rounded-l-lg text-gray-400">
                    <MapPin className="w-3.5 h-3.5" />
                  </span>
                  <Input
                    value={citySearch}
                    onChange={(e) => handleCitySearch(e.target.value)}
                    onFocus={() => citySearch.length >= 2 && setShowCitySuggestions(true)}
                    placeholder="City *"
                    className="rounded-l-none h-9 text-sm bg-gray-50 border-gray-200 rounded-r-lg"
                    data-testid="early-access-city"
                  />
                </div>
                {showCitySuggestions && citySuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                    {citySuggestions.map((city, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => selectCity(city)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      >
                        {typeof city === 'string' ? city : city.description}
                      </button>
                    ))}
                  </div>
                )}
                {/* Quick city buttons */}
                {!form.city && !showCitySuggestions && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {popularCities.slice(0, 4).map(city => (
                      <button
                        key={city}
                        type="button"
                        onClick={() => selectCity(city)}
                        className="px-2 py-0.5 text-[10px] bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600"
                      >
                        {city.split(',')[0]}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Services - Compact 6x2 Grid */}
              <div>
                <p className="text-[10px] font-medium text-gray-500 mb-1.5" data-testid="early-access-services-label">{slotText(pw, "field_services_label", "Services you offer")} *</p>
                <div className="grid grid-cols-6 gap-1.5">
                  {serviceOptions.map(service => {
                    const Icon = service.icon;
                    const isSelected = form.services.includes(service.id);
                    // Iter 9.48 - use Airtable colors when available
                    const useAirtableColors = service.darkHex && service.lightHex;
                    const selectedStyle = useAirtableColors && isSelected
                      ? { backgroundColor: service.lightHex, color: service.darkHex, borderColor: service.darkHex }
                      : undefined;
                    return (
                      <button
                        type="button"
                        key={service.id}
                        onClick={() => handleServiceToggle(service.id)}
                        style={selectedStyle}
                        className={`flex flex-col items-center gap-0.5 p-1.5 rounded-lg border transition-all ${
                          isSelected
                            ? (useAirtableColors ? "" : `${service.color} border-current`)
                            : "bg-gray-50 border-gray-100 text-gray-400 hover:border-gray-200"
                        }`}
                        data-testid={`service-${service.id}`}
                      >
                        {service.logoUrl ? (
                          <img
                            src={service.logoUrl}
                            alt=""
                            className="w-5 h-5 rounded object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <Icon className="w-4 h-4" />
                        )}
                        <span className="text-[8px] font-medium leading-tight">{service.label}</span>
                      </button>
                    );
                  })}
                </div>
                
                {/* Other Service Description Field */}
                {form.services.includes("other") && (
                  <div className="mt-2">
                    <Input
                      value={form.otherServiceDescription}
                      onChange={(e) => setForm(prev => ({ ...prev, otherServiceDescription: e.target.value }))}
                      placeholder="What service do you offer?"
                      className="h-9 text-sm bg-gray-50 border-gray-200 rounded-lg"
                      data-testid="other-service-description"
                    />
                  </div>
                )}
              </div>

              {/* Terms - Single Combined Checkbox */}
              <div className="flex items-start gap-2 py-2 px-3 bg-gray-50 rounded-lg">
                <Checkbox
                  id="ea-terms"
                  checked={form.agreedToTerms}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, agreedToTerms: checked }))}
                  className="mt-0.5"
                  data-testid="terms-consent-checkbox"
                />
                <label htmlFor="ea-terms" className="text-[10px] text-gray-600 leading-snug cursor-pointer">
                  I agree to receive updates and accept the{" "}
                  <a href="/terms" target="_blank" className="underline">Terms</a>,{" "}
                  <a href="/privacy" target="_blank" className="underline">Privacy</a>,{" "}
                  <a href="https://www.beehiiv.com/tou" target="_blank" rel="noopener noreferrer" className="underline">Beehiiv TOU</a> &{" "}
                  <a href="https://www.beehiiv.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Beehiiv Privacy</a>
                </label>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !isFormValid}
                className="w-full h-10 bg-black text-white hover:bg-gray-800 rounded-lg font-medium text-sm disabled:opacity-50"
                data-testid="submit-early-access-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Joining...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Get Early Access
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                )}
              </Button>
            </form>
          </div>
        ) : (
          <div className="text-center py-8 px-5">
            <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Check className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-1.5">
              {smartRoute?.launch_status === "launched" ? t("waitlist.popup.success_title_launched", "You're cleared to start!") : t("waitlist.popup.success_title", "You're on the List!")}
            </h3>
            <p className="text-gray-500 text-sm mb-2" data-testid="early-access-success-message">
              {smartRoute?.message || (
                <>We&apos;ll notify you when your {category ? (CATEGORY_SALES_LANGUAGE[category]?.headline?.replace("Join as a ", "").replace("Join as an ", "") || "provider") : "provider"} account is ready.</>
              )}
            </p>
            <p className="text-gray-600 text-xs mb-5 bg-gray-50 rounded-lg p-3">
              {smartRoute?.launch_status === "launched" ? (
                <><strong>Next step:</strong> Complete your host intake so we can auto-generate your dashboard.</>
              ) : (
                <><strong>Next step:</strong> Set up your consumer profile now so you can experience Phygital as a client first. This helps you understand what your clients will see.</>
              )}
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => {
                  // Iter 9.36 - always send waitlist users to /me dashboard with status banner
                  const dest = smartRoute?.destination || "/me?waitlist=joined";
                  resetAndClose();
                  navigate(dest);
                }}
                className="w-full bg-black text-white hover:bg-gray-800 h-10 rounded-lg text-sm"
                data-testid="post-waitlist-primary-cta"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                {smartRoute?.launch_status === "launched" ? t("waitlist.popup.cta_launched", "Continue to Host Intake") : t("waitlist.popup.cta_waitlist", "Continue to my Dashboard")}
              </Button>
              <Button variant="ghost" onClick={resetAndClose} className="w-full text-gray-500 text-sm h-9" data-testid="post-waitlist-dismiss">
                I&apos;ll do this later
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EarlyAccessModal;
